// Product photo helpers shared by the admin product manager and the customer
// menu. Photos are stored in MongoDB and served from /api/products/:id/image.

export const PHOTO_MAX_BYTES = 4 * 1024 * 1024;
export const PHOTO_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Stored photo URLs are root-relative ("/api/products/:id/image?v=..."), so
// they must be resolved against the API origin, not the page origin: in
// production the browser loads the app from the gateway (:80) while the API
// may answer on another origin. External URLs pass through untouched, and a
// relative REACT_APP_API_URL (same-origin gateway setups) resolves naturally.
export const resolveProductImageUrl = (imageUrl?: string | null): string => {
  if (!imageUrl) return '';
  if (/^https?:\/\//i.test(imageUrl)) return imageUrl;
  if (imageUrl.startsWith('/')) {
    try {
      return new URL(API_URL).origin + imageUrl;
    } catch {
      return imageUrl;
    }
  }
  return imageUrl;
};

// Returns a Spanish error message, or null when the file can be uploaded.
export const validatePhotoFile = (file: File | null | undefined): string | null => {
  if (!file) return 'Selecciona un archivo de foto.';
  if (!PHOTO_ALLOWED_TYPES.includes(file.type)) {
    return 'Formato no soportado. Usa JPG, PNG o WebP.';
  }
  if (file.size > PHOTO_MAX_BYTES) {
    return 'La foto supera el tamaño máximo de 4 MB.';
  }
  return null;
};

// Phone cameras deliver multi-megabyte photos that would be wasted bytes on a
// ~112 px menu card (and slow on mobile data). Large images are downscaled to
// at most 1200 px and re-encoded as JPEG; small, already-supported files pass
// through untouched.
const MAX_DIMENSION = 1200;
const REENCODE_THRESHOLD_BYTES = 512 * 1024;

const loadImageElement = (file: Blob): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode-failed')); };
    img.src = url;
  });

const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new Error('encode-failed'))),
      'image/jpeg',
      0.85
    );
  });

export const prepareProductPhoto = async (file: File): Promise<Blob> => {
  const img = await loadImageElement(file);
  const { naturalWidth: width, naturalHeight: height } = img;
  const needsResize = Math.max(width, height) > MAX_DIMENSION;
  if (!needsResize && file.size <= REENCODE_THRESHOLD_BYTES) {
    return file;
  }
  const scale = needsResize ? MAX_DIMENSION / Math.max(width, height) : 1;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvasToBlob(canvas);
};
